/**
 * 患者数据映射器
 *
 * 负责 API 响应和领域实体之间的转换
 *
 * @module patient-management/infrastructure/mappers
 */

import type {
  PatientApiResponse,
  CreatePatientRequest,
  UpdatePatientRequest,
} from "../infrastructure/patientApi";
import type {
  Patient,
  PatientSummary,
  CreatePatientParams,
  UpdatePatientParams,
} from "@shared/domain/patientTypes";
import { PatientStatus } from "@shared/domain/PatientStatus";
import { calculateAge } from "@shared/domain/patientTypes";
import {
  normalizePatientGender,
  type PatientGender,
} from "@patient-management/shared/domain/patientStandard";

/**
 * API 响应转领域实体
 */
export function mapApiToEntity(api: PatientApiResponse): Patient {
  const id = api._id || api.id || "";
  const birthDate = api.birth_date;

  return {
    id,
    patientId: api.patient_id,
    name: api.name || "未知",
    gender: mapGender(api.gender),
    birthDate,
    age: api.age ?? (birthDate ? calculateAge(birthDate) : undefined),
    phone: api.phone || api.phone_number,
    email: api.email,
    address: api.address,
    idCard: api.id_card || api.id_card_number,
    emergencyContact: api.emergency_contact,
    emergencyPhone: api.emergency_phone,
    wechatOpenid: api.wechat_openid,
    status: mapStatus(api.status),
    notes: api.notes,
    tags: api.tags,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

/**
 * API 响应转摘要
 */
export function mapApiToSummary(api: PatientApiResponse): PatientSummary {
  const birthDate = api.birth_date;

  return {
    id: api._id || api.id || "",
    patientId: api.patient_id,
    name: api.name || "未知",
    gender: mapGender(api.gender),
    age: api.age ?? (birthDate ? calculateAge(birthDate) : undefined),
    phone: api.phone || api.phone_number,
    wechatOpenid: api.wechat_openid,
    status: mapStatus(api.status),
  };
}

/**
 * 出生日期规范化为后端可解析格式
 *
 * - Date 对象：按本地时区取年月日（避免 toISOString 的 UTC 偏移导致生日差一天）
 * - 字符串：直传（YYYY-MM-DD 由后端 schema 容错补齐时间）
 */
function normalizeBirthDate(value: string | Date): string {
  if (value instanceof Date) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  return value;
}

/**
 * 创建参数转 API 请求
 * 字段名与后端 PatientCreate schema 匹配（标准名：phone, id_card）
 */
export function mapCreateParamsToRequest(
  params: CreatePatientParams,
): CreatePatientRequest {
  const request: CreatePatientRequest = {
    name: params.name,
    gender: params.gender || "未知", // 未提供时按 GB/T 2261.1「未知的性别」
    phone_number: params.phone || "", // 未提供时发送空串，由后端校验
  };
  // 可选字段：仅在有值时发送，避免后端存空串
  if (params.birthDate)
    request.birth_date = normalizeBirthDate(params.birthDate);
  if (params.email) request.email = params.email;
  if (params.address) request.address = params.address;
  if (params.idCard) request.id_card_number = params.idCard;
  if (params.emergencyContact)
    request.emergency_contact = params.emergencyContact;
  if (params.emergencyPhone) request.emergency_phone = params.emergencyPhone;
  if (params.notes) request.notes = params.notes;
  return request;
}

/**
 * 更新参数转 API 请求
 * 字段名与后端 PatientUpdate schema 匹配（标准名 phone/id_card，通过 alias 兼容旧名）
 */
export function mapUpdateParamsToRequest(
  params: UpdatePatientParams & {
    phone_number?: string;
    birth_date?: string;
    id_card_number?: string;
  },
): UpdatePatientRequest {
  // 兼容旧版字段名 phone_number 和新版 phone；
  // 含 * 的值必然为脱敏占位（手机号只含数字），不回传以避免污染档案
  const rawPhone = params.phone_number || params.phone;
  const phoneForRequest =
    rawPhone && rawPhone.includes("*") ? undefined : rawPhone;

  // 兼容旧版字段名 id_card_number 和新版 idCard；
  // 含 * 的值必然为脱敏占位，不回传以避免污染档案
  const rawIdCard = params.id_card_number || params.idCard;
  const idCardForRequest =
    rawIdCard && rawIdCard.includes("*") ? undefined : rawIdCard;

  return {
    name: params.name,
    gender: params.gender,
    // 兼容旧版字段名 birth_date 和新版 birthDate
    birth_date:
      params.birth_date ||
      (params.birthDate ? normalizeBirthDate(params.birthDate) : undefined),
    phone_number: phoneForRequest,
    email: params.email,
    address: params.address,
    id_card_number: idCardForRequest,
    emergency_contact: params.emergencyContact,
    emergency_phone: params.emergencyPhone,
    status: params.status,
    notes: params.notes,
  };
}

/**
 * 领域实体转 API 请求
 */
export function mapEntityToApiRequest(entity: Patient): UpdatePatientRequest {
  return {
    name: entity.name,
    gender: entity.gender,
    birth_date: entity.birthDate
      ? normalizeBirthDate(entity.birthDate)
      : undefined,
    phone_number: entity.phone, // 兼容后端 alias
    email: entity.email,
    address: entity.address,
    id_card_number: entity.idCard, // 兼容后端 alias
    emergency_contact: entity.emergencyContact,
    emergency_phone: entity.emergencyPhone,
    status: entity.status,
    notes: entity.notes,
  };
}

// ==================== 辅助函数 ====================

/**
 * 映射性别（后端存储 GB/T 2261.1 中文名称，前端兼容英文与历史值）
 */
function mapGender(gender?: string): PatientGender {
  return normalizePatientGender(gender);
}

/**
 * 映射状态
 */
function mapStatus(status?: string): PatientStatus {
  if (!status) return PatientStatus.ACTIVE;

  switch (status.toLowerCase()) {
    case "active":
    case "活跃":
      return PatientStatus.ACTIVE;
    case "inactive":
    case "非活跃":
      return PatientStatus.INACTIVE;
    case "archived":
    case "已归档":
      return PatientStatus.ARCHIVED;
    case "deceased":
    case "已故":
      return PatientStatus.DECEASED;
    default:
      return PatientStatus.ACTIVE;
  }
}

/**
 * 批量转换 API 响应到实体
 */
export function mapApiListToEntities(apiList: PatientApiResponse[]): Patient[] {
  return apiList.map(mapApiToEntity);
}

/**
 * 批量转换 API 响应到摘要
 */
export function mapApiListToSummaries(
  apiList: PatientApiResponse[],
): PatientSummary[] {
  return apiList.map(mapApiToSummary);
}
